/**
 * "Congés à valider" — the pending leave requests the viewer may answer, listed above the
 * Congés table so a manager does not have to filter the table by status to find them.
 * Same look as the arrêts' WorkStoppagePendingRequests panel.
 *
 * Validating is a direct status change, as in the table's actions. Refusing asks for a
 * second click, since it notifies the collab.
 *
 * Collapsed by default — it sits above the table and must not push it off screen.
 */
import React from "react";
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button, Chip, Stack, Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import { confirmDialog } from "../popups/ConfirmDialog";
import { getDayPeriodFromDate } from "../../app/utils/helpers";
import { STATUS_PALETTE } from "../workStoppages/WorkStoppageStatus";

function formatBound(timestamp) {
  if (!timestamp) return "";
  const period = getDayPeriodFromDate(timestamp);
  return `${moment(timestamp).format("DD/MM/YYYY")}${period ? ` ${period}` : ""}`;
}

/**
 * @param {Object} props
 * @param {Array} props.requests - pending leaves from `state.leaves.list`, already limited to
 *   those the viewer manages
 * @param {Function} props.onValidate - (leave) => void
 * @param {Function} props.onReject - (leave) => void
 * @param {Function} [props.onOpen] - (leave) => void, opens the leave's details
 * @param {Object} [props.sx]
 */
export default function LeavePendingRequests({ requests, onValidate, onReject, onOpen, sx }) {
  // "Refuser" asks first: a refused request cannot be taken back from here.
  const askReject = (leave) => {
    confirmDialog({
      title: <Langs str="REJECT_REQUEST_TITLE" />,
      message: <Langs str="REJECT_LEAVE_REQUEST_MSG" params={{ NAME: `${leave.prenom || ""} ${leave.nom || ""}`.trim() }} />,
      danger: true,
      icon: BlockRoundedIcon,
      confirmLabel: <Langs str="LEAVE_REJECT_CONFIRM" />,
      onConfirm: () => onReject(leave),
    });
  };

  if (!requests || !requests.length) return null;

  const count = requests.length;
  const title = count === 1
    ? Localize("LEAVE_PENDING_TITLE_ONE")
    : Localize("LEAVE_PENDING_TITLE_MANY").replace("%n%", count);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${STATUS_PALETTE.PENDING.fg}55`,
        borderRadius: "12px !important",
        bgcolor: STATUS_PALETTE.PENDING.bg,
        "&:before": { display: "none" },
        ...sx,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HourglassTopRoundedIcon fontSize="small" sx={{ color: STATUS_PALETTE.PENDING.fg }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: STATUS_PALETTE.PENDING.fg }}>
            {title}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          <Langs str="LEAVE_PENDING_HELP" />
        </Typography>
        <Box sx={{ maxHeight: 260, overflowY: "auto", bgcolor: "background.paper", borderRadius: "10px" }}>
          {requests.map((leave, index) => (
            <Stack
              key={leave.id}
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={{ px: 1.5, py: 1, borderTop: index ? "1px solid" : "none", borderColor: "divider" }}
            >
              <Box
                sx={{ flex: 1, minWidth: 0, cursor: onOpen ? "pointer" : "default" }}
                onClick={onOpen ? () => onOpen(leave) : undefined}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {`${leave.prenom || ""} ${leave.nom || ""}`}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    {`${formatBound(leave.fromDate)} → ${formatBound(leave.toDate)}`}
                  </Typography>
                  {leave.type ? (
                    <Chip size="small" label={leave.type} sx={{ height: 20, fontSize: "0.7rem", borderRadius: "6px" }} />
                  ) : null}
                  {leave.duration ? (
                    <Typography variant="caption" color="text.secondary">{leave.duration}</Typography>
                  ) : null}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button size="small" color="error" onClick={() => askReject(leave)}>
                  <Langs str="REJECT" />
                </Button>
                <Button size="small" variant="contained" color="warning" onClick={() => onValidate(leave)}>
                  <Langs str="VALIDATE" />
                </Button>
              </Stack>
            </Stack>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
